import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Users,
  Zap,
  Mail,
  Target,
  Globe,
  Monitor
} from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import Navbar from './shared/Navbar.jsx';
import Footer from './shared/Footer.jsx';
import HighlightBox from './HighlightBox.jsx';
import { sharedStyles, createGlassCard, createButton } from './shared/sharedStyles.js';
import { useApi } from '../utils/api.js';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isSignedIn, user, isLoaded } = useUser();
  const { makeRequest } = useApi();
  const [loading, setLoading] = useState(null);

  // Redirect authenticated users with subscriptions to dashboard
  // Users without subscriptions stay on landing page to complete checkout
  useEffect(() => {
    let isMounted = true;
    let redirectTimer = null;

    const handleAuthenticatedUser = async () => {
      console.log('🎬 useEffect running - isLoaded:', isLoaded, 'isSignedIn:', isSignedIn, 'user:', !!user);

      // Only proceed if user is loaded and signed in
      if (!isLoaded || !isSignedIn || !user) {
        console.log('⏸️  Waiting for auth to load...');
        return;
      }

      console.log('✅ User authenticated, checking subscription status...');

      try {
        // Check if user has subscription before redirecting
        const userData = await makeRequest('users/me');

        if (!isMounted) return;

        console.log('📊 User data received:', {
          email: userData.email,
          subscription_tier: userData.subscription_tier,
          is_lifetime: userData.is_lifetime,
          stripe_subscription_id: userData.stripe_subscription_id,
          stripe_customer_id: userData.stripe_customer_id
        });

        const hasSubscription = userData.subscription_tier && (
          userData.is_lifetime || userData.stripe_subscription_id
        );

        if (hasSubscription) {
          console.log('✅ User has subscription, redirecting to dashboard...');
          redirectTimer = setTimeout(() => {
            if (isMounted) {
              navigate('/dashboard');
            }
          }, 100);
        } else {
          console.log('ℹ️  User authenticated but no subscription - staying on landing page for checkout');
          console.log('   - subscription_tier:', userData.subscription_tier);
          console.log('   - is_lifetime:', userData.is_lifetime);
          console.log('   - stripe_subscription_id:', userData.stripe_subscription_id);
          // User stays on landing page to complete subscription
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        // On error, stay on landing page
      }
    };

    handleAuthenticatedUser();

    return () => {
      isMounted = false;
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [isLoaded, isSignedIn, user, navigate, makeRequest]);

  const createCheckoutSession = async (priceId, planName) => {
    console.log('💳 createCheckoutSession called - Plan:', planName, 'Price ID:', priceId);
    setLoading(planName);

    try {
      console.log('📡 Making API request to billing/create-checkout-session...');
      // Call backend to create checkout session using API utility
      const { url } = await makeRequest('billing/create-checkout-session', {
        method: 'POST',
        body: JSON.stringify({
          price_id: priceId,
          success_url: `${window.location.origin}/success`,
          cancel_url: `${window.location.origin}/#pricing`,
        }),
      });

      console.log('✅ Got checkout URL:', url);
      // Redirect to Stripe Checkout
      console.log('🔄 Redirecting to Stripe...');
      window.location.href = url;
    } catch (error) {
      console.error('❌ Checkout error:', error);
      alert(`Failed to start checkout: ${error.message}`);
      setLoading(null);
    }
  };



  const handleSubscribe = async (priceId, planName) => {
    if (!isSignedIn) {
      // Store intent and redirect to sign-up, which will then redirect to checkout-callback
      localStorage.setItem('pendingCheckout', JSON.stringify({ priceId, planName }));
      window.location.href = '/sign-up?redirect_url=' + encodeURIComponent('/checkout-callback');
      return;
    }

    // User is signed in, proceed with checkout
    await createCheckoutSession(priceId, planName);
  };

  const pricingTiers = [
    {
      name: 'Free',
      price: '$0',
      period: '',
      description: 'Try it out — no commitment',
      features: [
        '3 reports (15 guests)',
        'AI-powered guest discovery',
        'Relevance & attainability scoring',
        'Detailed guest summaries',
        'Expertise area mapping',
      ],
      notIncluded: [
        'Contact information',
        'Personalized pitches',
        'BYOK (unlimited reports)',
      ],
      priceId: null,
      cta: 'Get Started Free',
      popular: false,
      isFree: true,
      ctaAction: () => navigate('/sign-up'),
    },
    {
      name: 'Starter',
      price: '$19',
      period: '/month',
      description: 'Perfect for indie podcasters',
      features: [
        '8 reports/month (40 guests)',
        'AI-powered guest discovery',
        'Relevance & attainability scoring',
        'Detailed guest summaries',
        'Expertise area mapping',
      ],
      notIncluded: [
        'Contact information',
        'Personalized pitches',
        'BYOK (unlimited reports)',
      ],
      priceId: import.meta.env.VITE_STRIPE_PRICE_STARTER_MONTHLY,
      cta: 'Start Starter Plan',
      popular: false,
    },
    {
      name: 'Pro',
      price: '$49',
      period: '/month',
      description: 'For active podcasters',
      features: [
        '25 reports/month (125 guests)',
        'Everything in Starter, plus:',
        '✨ Email, LinkedIn, X/Twitter',
        '✨ Personalized outreach pitches',
        '✨ BYOK option (unlimited reports)',
        'Priority support',
      ],
      notIncluded: [],
      priceId: import.meta.env.VITE_STRIPE_PRICE_PRO_MONTHLY,
      cta: 'Start Pro Plan',
      popular: false,
    },
    {
      name: 'Pro Lifetime',
      price: '$490',
      period: 'one-time',
      description: 'Unlimited access, BYOK required',
      savings: 'Break-even in 10 months',
      features: [
        'All Pro features forever',
        'Unlimited reports (BYOK required)',
        '✨ Email, LinkedIn, X/Twitter',
        '✨ Personalized outreach pitches',
        '✨ You provide Anthropic API key',
        '~$1.40/report to Anthropic directly',
        'Priority support',
      ],
      notIncluded: [],
      priceId: import.meta.env.VITE_STRIPE_PRICE_PRO_LIFETIME,
      cta: 'Get Lifetime Access',
      popular: false,
      popular: true,
      badgeText: 'BEST DEAL',
    },
  ];

  const features = [
    {
      icon: <Search className="w-6 h-6" />,
      title: "AI-Powered Guest Discovery",
      description: "Our advanced AI searches the web to find perfect guests based on your show's niche and ideal guest profile."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Comprehensive Guest Profiles",
      description: "Get detailed profiles with bio, expertise areas, previous podcast appearances, and relevance scoring."
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Contact Information",
      description: "Find social media profiles and email addresses to reach out to potential guests directly."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Personalized Pitch Generation",
      description: "AI-generated personalized pitches tailored to each guest and your show's unique value proposition."
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Smart Matching",
      description: "AI scores each guest on relevance to your topics and attainability—their likelihood of accepting your interview request based on your show's reach."
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Web-Wide Search",
      description: "Search across podcasts, social media, professional networks, and websites to find hidden gems."
    }
  ];

  // Removed fake testimonials - will add real ones later

  return (
    <div style={{ minHeight: '100vh', background: sharedStyles.colors.background }}>
      {/* Header */}
      <Navbar variant="landing" showNav={true} />

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <h1>
            Find perfect <br />
            <span className="highlight">podcast</span>{' '}guests
          </h1>
          <p>
            Stop spending hours searching. Our AI discovers, evaluates, and connects you
            with guests who align perfectly with your show.
          </p>

          {/* CTA Button */}
          <div style={{ maxWidth: '28rem', margin: '0 auto 2rem' }}>
            <button
              onClick={() => navigate('/sign-up')}
              className="btn-primary"
              style={{
                padding: '1rem 2.5rem',
                fontSize: '1.125rem',
                fontWeight: '600'
              }}
            >
              Get Started
            </button>
          </div>

          {/* <p className="waitlist-count">
            Be the first to know when we launch
          </p> */}

          {/* App Preview with Screenshots */}
          <div className="app-preview">
            <div className="preview-content">
              <div className="preview-header">
                <h3 className="preview-title">Ideal Guest Discovery</h3>
                <Monitor style={{ width: '1.5rem', height: '1.5rem', color: sharedStyles.colors.textTertiary }} />
              </div>
              <div className="preview-grid" style={{ gridTemplateColumns: '1fr' }}>
                <img
                  src="/guest-report.png"
                  alt="Conviviality guest discovery report showing AI-matched podcast guests with relevance scores, expertise areas, and contact information"
                  width="1200"
                  height="800"
                  loading="eager"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section - Social Proof */}
      <section className="section" style={{ paddingTop: '3rem', paddingBottom: '3rem', background: sharedStyles.colors.backgroundAlt }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="section-title">Save 10+ Hours Per Week on Guest Research</h2>
            <p className="section-subtitle">
              Let AI do the heavy lifting while you focus on creating great content
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '2rem',
            textAlign: 'center'
          }}>
            <div style={createGlassCard('default', { padding: '2rem' })}>
              <div style={{
                fontSize: '2.5rem',
                fontWeight: '800',
                background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '0.5rem'
              }}>
                ~1 min
              </div>
              <div style={{
                fontSize: '1rem',
                color: sharedStyles.colors.textSecondary,
                fontWeight: '600'
              }}>
                AI Discovery
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: sharedStyles.colors.textTertiary,
                marginTop: '0.5rem'
              }}>
                vs 10+ hours manually
              </div>
            </div>

            <div style={createGlassCard('default', { padding: '2rem' })}>
              <div style={{
                fontSize: '2.5rem',
                fontWeight: '800',
                background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '0.5rem'
              }}>
                5
              </div>
              <div style={{
                fontSize: '1rem',
                color: sharedStyles.colors.textSecondary,
                fontWeight: '600'
              }}>
                Curated Guests
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: sharedStyles.colors.textTertiary,
                marginTop: '0.5rem'
              }}>
                Per report generated
              </div>
            </div>

            <div style={createGlassCard('default', { padding: '2rem' })}>
              <div style={{
                fontSize: '2.5rem',
                fontWeight: '800',
                background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '0.5rem'
              }}>
                Email
              </div>
              <div style={{
                fontSize: '1rem',
                color: sharedStyles.colors.textSecondary,
                fontWeight: '600'
              }}>
                & Social Links
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: sharedStyles.colors.textTertiary,
                marginTop: '0.5rem'
              }}>
                Included with Pro plan
              </div>
            </div>

            <div style={createGlassCard('default', { padding: '2rem' })}>
              <div style={{
                fontSize: '2.5rem',
                fontWeight: '800',
                background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: '0.5rem'
              }}>
                Custom
              </div>
              <div style={{
                fontSize: '1rem',
                color: sharedStyles.colors.textSecondary,
                fontWeight: '600'
              }}>
                Pitch Emails
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: sharedStyles.colors.textTertiary,
                marginTop: '0.5rem'
              }}>
                AI-written for each guest
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Conviviality Does */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="section-title">How Does AI Podcast Guest Discovery Work?</h2>
            <p className="section-subtitle">
              Conviviality is your AI-powered guest discovery platform.
              Simply describe your show and ideal guest profile, and we'll find perfectly aligned guests
              who'll create engaging conversations that resonate with your audience.
            </p>
          </div>

          <div className="grid-3" style={{ marginBottom: '4rem' }}>
            <div className="step-card">
              <div className="feature-icon">
                <Search style={{ width: '2rem', height: '2rem' }} />
              </div>
              <h3 className="step-title">Intelligent Search</h3>
              <p className="step-description">
                Our AI analyzes your show description and searches across podcasts, social media,
                professional networks, and websites to find relevant guests.
              </p>
            </div>

            <div className="step-card">
              <div className="feature-icon">
                <Users style={{ width: '2rem', height: '2rem' }} />
              </div>
              <h3 className="step-title">Detailed Analysis</h3>
              <p className="step-description">
                Get comprehensive guest profiles including expertise areas, previous podcast appearances,
                social media presence, and relevance scoring.
              </p>
            </div>

            <div className="step-card">
              <div className="feature-icon">
                <Mail style={{ width: '2rem', height: '2rem' }} />
              </div>
              <h3 className="step-title">Easy Outreach</h3>
              <p className="step-description">
                Find contact information and get AI-generated personalized pitches
                tailored to each guest and your show's unique value proposition.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section section-gray">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="section-title">What Features Help You Find Perfect Guests?</h2>
            <p className="section-subtitle">
              Everything you need to discover, evaluate, and connect with the perfect guests for your show.
            </p>
          </div>

          <div className="grid-3">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3 className="feature-title">
                  {feature.title}
                </h3>
                <p className="feature-description">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="section">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="section-title">How Do I Find Podcast Guests in 3 Simple Steps?</h2>
            <p className="section-subtitle">
              Three simple steps to find your next great guest
            </p>
          </div>

          <div className="grid-3">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3 className="step-title">Describe Your Show</h3>
              <p className="step-description">
                Tell us about your podcast, ideal guest profile, target audience, and what topics you cover.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <h3 className="step-title">AI Finds Guests</h3>
              <p className="step-description">
                Our AI searches the web and analyzes potential guests based on your criteria and show requirements.
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <h3 className="step-title">Get Your Report</h3>
              <p className="step-description">
                Receive detailed guest profiles with contact info, relevance scores, and personalized pitches.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section">
        <div className="container" style={{ maxWidth: '900px' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="section-title">About</h2>
            <p className="section-subtitle">
              The best interviews happen when hosts and guests share genuine chemistry.
            </p>
          </div>

          <div className="grid-2" style={{ gap: '3rem', marginBottom: '3rem' }}>
            <div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: sharedStyles.colors.textPrimary, marginBottom: '1rem' }}>
                Our Mission
              </h3>
              <p style={{ fontSize: '1rem', color: sharedStyles.colors.textSecondary, lineHeight: '1.8', marginBottom: '1rem' }}>
                Conviviality exists to solve one of podcasting's biggest challenges: finding the perfect guests.
                Every week, thousands of podcast hosts spend hours searching for guests who align with their show's
                vision, audience, and values.
              </p>
              <p style={{ fontSize: '1rem', color: sharedStyles.colors.textSecondary, lineHeight: '1.8' }}>
                By combining AI-powered research with human insight, we help podcasters discover guests who don't
                just fit the topic—they elevate the conversation.
              </p>
            </div>

            <div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: sharedStyles.colors.textPrimary, marginBottom: '1rem' }}>
                Why "Conviviality"?
              </h3>
              <p style={{ fontSize: '1rem', color: sharedStyles.colors.textSecondary, lineHeight: '1.8', marginBottom: '1rem' }}>
                The word "conviviality" captures what we're all about: friendly, lively, and enjoyable conversation.
                It's that warm feeling when you're talking with someone who just "gets it"—when the discussion flows
                naturally and both parties leave feeling energized.
              </p>
              <p style={{ fontSize: '1rem', color: sharedStyles.colors.textSecondary, lineHeight: '1.8' }}>
                That's the kind of interview experience we want to help you create, every single time.
              </p>
            </div>
          </div>

          <HighlightBox />
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="section" style={{ background: sharedStyles.colors.background, paddingTop: '3rem', paddingBottom: '3rem' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          {/* Pricing Header */}
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '0.5rem',
            }}>
              Choose Your Plan
            </h2>
            <p style={{
              fontSize: '0.95rem',
              color: sharedStyles.colors.textSecondary,
              maxWidth: '600px',
              margin: '0 auto',
            }}>
              Try 3 free reports to start. Monthly plans for regular discovery, or go lifetime for the best value.
            </p>
          </div>

          {/* Subscription Tiers Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1.5rem',
            marginBottom: '3rem',
          }}>
            {pricingTiers.map((tier) => (
              <div
                key={tier.name}
                style={{
                  ...createGlassCard('default', {
                    padding: '1.5rem',
                    position: 'relative',
                  }),
                }}
              >
                {/* Badge */}
                {tier.popular && (
                  <div style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '15px',
                    background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
                    color: 'white',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '16px',
                    fontSize: '0.65rem',
                    fontWeight: '600',
                    boxShadow: '0px 4px 12px rgba(234, 88, 12, 0.3)',
                  }}>
                    {tier.badgeText || 'POPULAR'}
                  </div>
                )}

                {/* Plan Name */}
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  color: sharedStyles.colors.textPrimary,
                  marginBottom: '0.5rem',
                }}>
                  {tier.name}
                </h3>

                {/* Price */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <span style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                    {tier.price}
                  </span>
                  <span style={{
                    fontSize: '0.875rem',
                    color: sharedStyles.colors.textSecondary,
                    marginLeft: '0.375rem',
                  }}>
                    {tier.period}
                  </span>
                </div>

                {/* Description */}
                <p style={{
                  fontSize: '0.8rem',
                  color: sharedStyles.colors.textSecondary,
                  marginBottom: '1rem',
                  minHeight: '36px',
                }}>
                  {tier.description}
                </p>

                {/* Savings (for Lifetime) */}
                {tier.savings && (
                  <div style={{
                    background: sharedStyles.colors.primaryLight,
                    color: sharedStyles.colors.primary,
                    padding: '0.375rem',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textAlign: 'center',
                    marginBottom: '1rem',
                  }}>
                    {tier.savings}
                  </div>
                )}

                {/* CTA Button */}
                <button
                  onClick={() => tier.ctaAction ? tier.ctaAction() : handleSubscribe(tier.priceId, tier.name)}
                  disabled={loading !== null}
                  style={{
                    ...createButton('primary', {
                      width: '100%',
                      justifyContent: 'center',
                      padding: '0.75rem',
                      fontSize: '0.875rem',
                      marginBottom: '1rem',
                      opacity: loading && loading !== tier.name ? 0.5 : 1,
                    }),
                  }}
                >
                  {loading === tier.name ? 'Loading...' : tier.cta}
                </button>

                {/* Features List */}
                <div style={{ borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '1rem' }}>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {tier.features.map((feature, index) => (
                      <li
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.5rem',
                          marginBottom: '0.5rem',
                          fontSize: '0.8rem',
                          color: sharedStyles.colors.textPrimary,
                        }}
                      >
                        <span style={{ color: sharedStyles.colors.success, fontSize: '1rem', lineHeight: '1.2' }}>
                          ✓
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                    {tier.notIncluded.map((feature, index) => (
                      <li
                        key={`not-${index}`}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.5rem',
                          marginBottom: '0.5rem',
                          fontSize: '0.8rem',
                          color: sharedStyles.colors.textMuted,
                          textDecoration: 'line-through',
                        }}
                      >
                        <span style={{ color: sharedStyles.colors.textMuted, fontSize: '1rem', lineHeight: '1.2' }}>
                          ✗
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* FAQ Section - GEO Optimization */}
      <section id="faq" className="section section-gray">
        <div className="container" style={{ maxWidth: '900px' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">
              Everything you need to know about AI-powered podcast guest discovery
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {/* FAQ 1 */}
            <div style={createGlassCard('default', { padding: '2rem' })}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: sharedStyles.colors.primary,
                marginBottom: '1rem'
              }}>
                How does AI-powered podcast guest discovery work?
              </h3>
              <p style={{
                fontSize: '1rem',
                color: sharedStyles.colors.textSecondary,
                lineHeight: '1.8'
              }}>
                Conviviality uses advanced AI to search across podcasts, social media, professional networks, and websites
                to find guests who match your show's niche and ideal guest profile. Our AI analyzes your show description,
                target audience, and topics covered, then searches the web for experts with relevant expertise. The AI
                evaluates each potential guest based on their background, speaking experience, online presence, and
                alignment with your audience to deliver 5+ highly relevant matches.
              </p>
            </div>

            {/* FAQ 2 */}
            <div style={createGlassCard('default', { padding: '2rem' })}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: sharedStyles.colors.primary,
                marginBottom: '1rem'
              }}>
                What information do I get about each potential podcast guest?
              </h3>
              <p style={{
                fontSize: '1rem',
                color: sharedStyles.colors.textSecondary,
                lineHeight: '1.8'
              }}>
                Each guest profile includes their name, title, company, expertise areas, a relevance score (0-100),
                and a detailed summary explaining why they're a perfect fit for your show. Pro subscribers also receive
                full contact information (email, LinkedIn, Twitter) and AI-generated personalized outreach pitches
                tailored to each guest, making it easy to start booking interviews immediately.
              </p>
            </div>

            {/* FAQ 3 */}
            <div style={createGlassCard('default', { padding: '2rem' })}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: sharedStyles.colors.primary,
                marginBottom: '1rem'
              }}>
                How much does AI podcast guest discovery cost?
              </h3>
              <p style={{
                fontSize: '1rem',
                color: sharedStyles.colors.textSecondary,
                lineHeight: '1.8'
              }}>
                Start with 3 free reports (15 AI-vetted guests, no credit card required) to test the quality. After that,
                choose from our subscription plans: Starter at $19/month (8 reports = 40 guests with basic features), Pro at $49/month
                (25 reports = 125 guests with contacts, pitches, and optional BYOK for unlimited), or Pro Lifetime at $785
                one-time (all Pro features forever, best value for committed podcasters). All plans save you hours of manual
                research and help you discover guests you would never find on your own.
              </p>
            </div>

            {/* FAQ 4 */}
            <div style={createGlassCard('default', { padding: '2rem' })}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: sharedStyles.colors.primary,
                marginBottom: '1rem'
              }}>
                How much time does Conviviality save me?
              </h3>
              <p style={{
                fontSize: '1rem',
                color: sharedStyles.colors.textSecondary,
                lineHeight: '1.8'
              }}>
                Each report saves you 2.5 hours of manual research—the AI generates 5 perfectly matched guests with contact
                info and pitches in just 30-60 seconds. Manual research typically takes 30 minutes per guest (finding them,
                vetting expertise, hunting for contact info, writing personalized pitches). At $50/hour, that's $125 in
                saved time per report. If you generate 10 reports, you save 25 hours and $1,200+. The ROI is immediate—your
                first report pays for itself.
              </p>
            </div>


            {/* FAQ 6 */}
            <div style={createGlassCard('default', { padding: '2rem' })}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: sharedStyles.colors.primary,
                marginBottom: '1rem'
              }}>
                What makes a good podcast guest match?
              </h3>
              <p style={{
                fontSize: '1rem',
                color: sharedStyles.colors.textSecondary,
                lineHeight: '1.8'
              }}>
                A great guest match combines expertise relevance, audience alignment, and realistic accessibility. Our AI
                evaluates potential guests on their domain expertise, speaking experience, content they've created, and how
                well their knowledge aligns with your show's topics. We also calculate an attainability score based on your
                show's audience size—ensuring we suggest guests who are likely to accept your interview request rather than
                celebrities who won't respond. The relevance score helps you prioritize topic fit, while attainability helps
                you focus outreach on guests you can actually book.
              </p>
            </div>

            {/* FAQ 7 */}
            <div style={createGlassCard('default', { padding: '2rem' })}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: sharedStyles.colors.primary,
                marginBottom: '1rem'
              }}>
                How do I contact guests after finding them?
              </h3>
              <p style={{
                fontSize: '1rem',
                color: sharedStyles.colors.textSecondary,
                lineHeight: '1.8'
              }}>
                Pro subscribers get full contact information for each guest: verified email addresses, LinkedIn profiles,
                and Twitter/X accounts. Even better, you get AI-generated personalized pitches for each guest that you can
                copy and customize. These pitches explain why the guest would be perfect for your show and make it easy to
                start professional outreach. Simply copy the pitch, add your scheduling link, and send via email or LinkedIn
                message.
              </p>
            </div>

            {/* FAQ 8 */}
            <div style={createGlassCard('default', { padding: '2rem' })}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: sharedStyles.colors.primary,
                marginBottom: '1rem'
              }}>
                What's the ROI compared to manual podcast guest research?
              </h3>
              <p style={{
                fontSize: '1rem',
                color: sharedStyles.colors.textSecondary,
                lineHeight: '1.8'
              }}>
                The ROI is immediate and substantial. Manual research costs $125 per report (2.5 hours at $50/hour) finding
                just 5 guests. Our Starter plan ($19/mo = 40 guests) brings that cost down to $0.48 per guest—a 98% savings.
                Pro plan ($49/mo = 125 guests) is even better at $0.39 per guest, plus includes contacts and pitches. That's
                a 200x speed improvement and near-total cost elimination. Plus, the AI searches exponentially more sources
                simultaneously (social media, podcasts, articles, websites) and provides objective relevance scoring. Your first
                month pays for itself.
              </p>
            </div>

            {/* FAQ 9 */}
            <div style={createGlassCard('default', { padding: '2rem' })}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: sharedStyles.colors.primary,
                marginBottom: '1rem'
              }}>
                Do I need a certain download count to use Conviviality?
              </h3>
              <p style={{
                fontSize: '1rem',
                color: sharedStyles.colors.textSecondary,
                lineHeight: '1.8'
              }}>
                No! Conviviality works for podcasters at every stage, from brand new shows to established programs with
                millions of downloads. Whether you're launching your first episode or booking your 500th guest, our AI
                finds relevant experts for your niche. Small shows often benefit most because guest discovery saves the
                most time when you're wearing all the hats. The quality of your guest match depends on your show's focus
                and audience, not your download numbers.
              </p>
            </div>

            {/* FAQ 10 */}
            <div style={createGlassCard('default', { padding: '2rem' })}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: sharedStyles.colors.primary,
                marginBottom: '1rem'
              }}>
                What's the difference between Starter and Pro plans?
              </h3>
              <p style={{
                fontSize: '1rem',
                color: sharedStyles.colors.textSecondary,
                lineHeight: '1.8'
              }}>
                Both plans include AI-powered guest discovery with relevance scores and detailed summaries. Starter ($19/mo =
                40 guests) gives you basic guest information perfect for initial discovery. Pro ($49/mo = 125 guests) includes
                everything in Starter plus full contact information (email, LinkedIn, Twitter), AI-generated personalized
                outreach pitches, and optional BYOK (bring your own Anthropic API key) for unlimited reports. Pro is ideal
                for active podcasters who need contacts and pitches to streamline their outreach workflow.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-started" className="section section-primary">
        <div className="container" style={{ maxWidth: '64rem', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'white', marginBottom: '1rem' }}>
            Ready to Find Your Perfect Guests?
          </h2>
          <p style={{ fontSize: '1.25rem', color: 'white', marginBottom: '2rem' }}>
            Join Conviviality today and start discovering guests in seconds.
          </p>

          {/* CTA Button */}
          <div style={{ maxWidth: '28rem', margin: '0 auto' }}>
            <button
              onClick={() => navigate('/sign-up')}
              style={{
                background: 'white',
                color: 'var(--accent-primary)',
                fontWeight: '600',
                fontSize: '1.125rem',
                padding: '1rem 2.5rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                boxShadow: '0px 4px 16px rgba(255, 255, 255, 0.3)'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = sharedStyles.colors.surfaceAlt;
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0px 8px 32px rgba(255, 255, 255, 0.5)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0px 4px 16px rgba(255, 255, 255, 0.3)';
              }}
            >
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;
